import * as React from "react";
import { ContentType, HttpVerb, requestWithAuth } from "../../../utils/httpRequest";
import AppContext from "../AppContext";
import api from "../../api";
import { Callout, DirectionalHint, Spinner, SpinnerSize, TooltipHost } from "office-ui-fabric-react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faClock, faComment, faEnvelope } from "@fortawesome/free-solid-svg-icons";
import { OdooTheme } from "../../../utils/Themes";
import { _t } from "../../../utils/Translator";
import LogCommentInput from "./LogCommentInput";
import Partner from "../../../classes/Partner";
import { formatOdooDateTime, getOriginalMailDate } from "../../../utils/mailItemDate";
import { extractRecordId } from "../../../utils/mailMessage";

//total attachments size threshold in megabytes
const SIZE_THRESHOLD_TOTAL = 40;

//single attachment size threshold in megabytes
const SIZE_THRESHOLD_SINGLE_ELEMENT = 10;

type LoggerProps = {
  resId: number;
  model: string;
  partner: Partner;
  tooltipMailContent: string;
};

type LoggerState = {
  logged: number;
  isCallOutOpen: boolean;
  createCallback?: (any?) => {};
};

class Logger extends React.Component<LoggerProps, LoggerState> {
  static contextType = AppContext;
  context!: any; // AppContext is JS, so we use any

  constructor(props, context) {
    super(props, context);
    this.state = {
      logged: 0,
      isCallOutOpen: false
    };
  }

  private isTargetQueued = (): boolean => {
    if (!this.context.isComposeMode) return false;
    return this.context.composeLogTargets.some(
      t => t.model === this.props.model && t.resId === this.props.resId
    );
  };

  private toggleComposeLogTarget = (event): void => {
    event.stopPropagation();
    const target = {
      model: this.props.model,
      resId: this.props.resId,
      label: this.props.partner?.name || `${this.props.model} #${this.props.resId}`
    };

    if (this.isTargetQueued()) {
      this.context.removeComposeLogTarget(target.resId, target.model);
    } else {
      this.context.addComposeLogTarget(target);
    }
  };

  private base64EncodeUnicode(value: string): string {
    // btoa only supports Latin1; this converts UTF-8 safely.
    try {
      return btoa(unescape(encodeURIComponent(value)));
    } catch (error) {
      // As a last resort, try a direct btoa (may still throw).
      return btoa(value as any);
    }
  }

  private getInlineImageMimeType(attachment: { contentType?: string; name?: string }): string {
    if (attachment.contentType && attachment.contentType.indexOf("/") >= 0) {
      return attachment.contentType;
    }

    const fileName = attachment.name || "";
    const fileExtension = fileName.includes(".") ? fileName.split(".").pop()?.toLowerCase() : "";
    switch (fileExtension) {
      case "jpg":
      case "jpeg":
        return "image/jpeg";
      case "gif":
        return "image/gif";
      case "bmp":
        return "image/bmp";
      case "svg":
        return "image/svg+xml";
      case "webp":
        return "image/webp";
      case "png":
      default:
        return "image/png";
    }
  }

  private isInlineImageAttachment(attachment: { isInline?: boolean; contentType?: string }): boolean {
    return Boolean(attachment.isInline && attachment.contentType && attachment.contentType.indexOf("image") >= 0);
  }

  private hideInlineImageElement(imageElement: Element, altText: string): void {
    const currentStyle = imageElement.getAttribute("style") || "";
    const styleSuffix = currentStyle && !currentStyle.trim().endsWith(";") ? ";" : "";
    imageElement.setAttribute("alt", altText);
    imageElement.removeAttribute("src");
    imageElement.setAttribute("style", `${currentStyle}${styleSuffix}display:none;`);
    imageElement.setAttribute("data-odoo-inline-image-hidden", "true");
  }

  private fetchAttachmentContent(attachment, index): Promise<any> {
    return new Promise<any>(resolve => {
      if (!attachment || !attachment.id) {
        resolve({
          name: attachment?.name || "unknown",
          contentType: attachment?.contentType || "",
          inline: false,
          oversize: false,
          index: index,
          error: "Attachment has no valid ID",
          format: null
        });
        return;
      }
      if (attachment.size > SIZE_THRESHOLD_SINGLE_ELEMENT * 1024 * 1024) {
        resolve({
          name: attachment.name,
          contentType: attachment.contentType,
          inline: this.isInlineImageAttachment(attachment),
          oversize: true,
          index: index,
          format: null
        });
        return;
      }
      Office.context.mailbox.item.getAttachmentContentAsync(attachment.id, asyncResult => {
        if (asyncResult.status !== Office.AsyncResultStatus.Succeeded || !asyncResult.value) {
          resolve({
            name: attachment.name,
            contentType: attachment.contentType,
            inline: this.isInlineImageAttachment(attachment),
            oversize: false,
            index: index,
            error: asyncResult.error || "Could not fetch attachment content",
            format: null
          });
          return;
        }

        const { content, format } = asyncResult.value;

        // Outlook can return:
        // - Base64: file content base64
        // - Url: cloud attachment link (cannot be uploaded as file without fetching)
        // - Eml: item attachment content as text
        let normalizedContent: string | null = null;
        if (format === Office.MailboxEnums.AttachmentContentFormat.Base64) {
          normalizedContent = content;
        } else if (format === Office.MailboxEnums.AttachmentContentFormat.Eml && typeof content === "string") {
          normalizedContent = this.base64EncodeUnicode(content);
        } else {
          normalizedContent = null; // Url or unknown format
        }

        resolve({
          name: attachment.name,
          contentType: attachment.contentType,
          content: normalizedContent,
          inline: this.isInlineImageAttachment(attachment),
          oversize: false,
          index: index,
          format
        });
      });
    });
  }

  private logRequest = async (event): Promise<any> => {
    event.stopPropagation();
    
    // In compose mode, logRequest should not be called directly
    // (use toggleComposeLogTarget instead)
    if (this.context.isComposeMode) {
      console.warn("[Logger] logRequest called in compose mode - this should not happen");
      return;
    }

    // READ MODE: Log immediately (original behavior)
    this.setState({ logged: 1 });
    Office.context.mailbox.item.body.getAsync(Office.CoercionType.Html, async result => {
      try {
        if (result.status !== Office.AsyncResultStatus.Succeeded) {
          throw result.error || new Error("Could not read email body");
        }

        const item = Office.context.mailbox.item as any;

        // Helper: resolve an async Office recipient object or plain value
        const getAsyncStr = (obj: any): Promise<string> => new Promise(resolve => {
          if (typeof obj?.getAsync === "function") {
            obj.getAsync((r: any) => {
              const val = r.status === Office.AsyncResultStatus.Succeeded ? r.value : null;
              resolve(typeof val === "string" ? val : "");
            });
          } else {
            resolve(typeof obj === "string" ? obj : "");
          }
        });

        const getAsyncRecipients = (obj: any): Promise<any[]> => new Promise(resolve => {
          if (typeof obj?.getAsync === "function") {
            obj.getAsync((r: any) => {
              const val = r.status === Office.AsyncResultStatus.Succeeded ? r.value : null;
              resolve(Array.isArray(val) ? val : []);
            });
          } else {
            resolve(Array.isArray(obj) ? obj : []);
          }
        });

        const getAsyncFrom = (obj: any): Promise<any> => new Promise(resolve => {
          if (typeof obj?.getAsync === "function") {
            obj.getAsync((r: any) => {
              const val = r.status === Office.AsyncResultStatus.Succeeded ? r.value : null;
              resolve(val || {});
            });
          } else {
            resolve(obj || {});
          }
        });

        const [itemSubject, fromRaw, toResult, ccResult, bccResult] = await Promise.all([
          getAsyncStr(item.subject),
          getAsyncFrom(item.from || item.sender),
          getAsyncRecipients(item.to),
          getAsyncRecipients(item.cc),
          getAsyncRecipients(item.bcc),
        ]);

        // Outlook Web returns emailAddress and displayName but sometimes as different keys
        // Use &lt; &gt; so the email is not swallowed as an HTML tag in Odoo chatter
        const formatRecipient = (r: any): string => {
          const email = r.emailAddress || r.email || r.address || "";
          const name  = r.displayName || r.name || "";
          return name && email ? `${name} &lt;${email}&gt;` : email || name;
        };

        const fromLabel      = formatRecipient(fromRaw);
        const toRecipients  = toResult.map(formatRecipient).filter(Boolean).join(", ");
        const ccRecipients  = ccResult.map(formatRecipient).filter(Boolean).join(", ");
        const bccRecipients = bccResult.map(formatRecipient).filter(Boolean).join(", ");
        const originalDate = getOriginalMailDate(item);
        const sentDate = originalDate.toLocaleString();

        const msgHeader = [
          fromLabel      ? `<div><b>From:</b> ${fromLabel}</div>`      : "",
          toRecipients   ? `<div><b>To:</b> ${toRecipients}</div>`     : "",
          ccRecipients   ? `<div><b>CC:</b> ${ccRecipients}</div>`     : "",
          bccRecipients  ? `<div><b>BCC:</b> ${bccRecipients}</div>`   : "",
          itemSubject    ? `<div><b>Subject:</b> ${itemSubject}</div>` : "",
          `<div><b>Date:</b> ${sentDate}</div>`,
          "<br/>"
        ].join("");
        const msgFooter = `<br/><div class="text-muted font-italic">${_t("Logged from Outlook Inbox")}</div>`;
        let body = result.value.split('<div id="x_appendonsend"></div>')[0] ?? result.value; // Remove the history and only log the most recent message.
        // replace all p tags with div tags
        body = body.replace(/<p/g, "<div");
        body = body.replace(/<\/p>/g, "</div>");

        const message = msgHeader + body + msgFooter;

        const doc = new DOMParser().parseFromString(message, "text/html");
        const officeAttachmentDetails = (Office.context.mailbox.item.attachments ?? [])
          .filter(a => a && a.id != null);
        let totalSize = 0;
        const promises: any[] = [];
        const requestJson: any = {
          args: [
            {
              model: this.props.model,
              res_id: this.props.resId,
              body: message,
              subject: itemSubject,
              message_type: "email",
              date: formatOdooDateTime(new Date())
            }
          ]
        };

        //check if attachment size is bigger then the threshold
        officeAttachmentDetails.forEach(officeAttachment => {
          totalSize += officeAttachment.size;
        });

        if (totalSize > SIZE_THRESHOLD_TOTAL * 1024 * 1024) {
          const warningMessage = _t(
            "Warning: Attachments could not be logged in Odoo because their total size" +
              " exceeded the allowed maximum.",
            {
              size: SIZE_THRESHOLD_SINGLE_ELEMENT
            }
          );
          doc.body.innerHTML += `<div class="text-danger">${warningMessage}</div>`;
        } else {
          officeAttachmentDetails.forEach((attachment, index) => {
            promises.push(this.fetchAttachmentContent(attachment, index));
          });
        }

        const results = await Promise.all(promises);

        let attachments = [];
        let oversizeAttachments = [];
        let inlineAttachments = [];
        let unsupportedAttachments = [];
        let unsupportedInlineAttachments = [];

        results.forEach(result => {
          if (result.inline) {
            inlineAttachments[result.index] = result;
            return;
          }

          if (result.error) {
            unsupportedAttachments.push({
              name: result.name
            });
          } else if (result.oversize) {
            oversizeAttachments.push({
              name: result.name
            });
          } else if (!result.content) {
            // Url/unknown formats: can't be reliably uploaded from add-in context.
            unsupportedAttachments.push({
              name: result.name
            });
          } else {
            attachments.push({
              name: result.name,
              content: result.content,
              contentType: result.contentType
            });
          }
        });
        // a counter is needed to map img tags with attachments, as outlook does not provide
        // an id that enables us to match an img with an attachment.
        let j = 0;
        const imageElements = doc.getElementsByTagName("img");

        inlineAttachments.forEach(inlineAttachment => {
          if (inlineAttachment != null) {
            const imageElement = imageElements[j];
            if (!imageElement) {
              console.warn("Inline attachment could not be mapped to an img tag.", inlineAttachment);
              unsupportedInlineAttachments.push({
                name: inlineAttachment.name
              });
              return;
            }

            if (inlineAttachment.oversize) {
              this.hideInlineImageElement(
                imageElement,
                _t("Could not display image %(attachmentName)s, size is over limit", {
                  attachmentName: inlineAttachment.name
                })
              );
              unsupportedInlineAttachments.push({
                name: inlineAttachment.name,
                reason: "oversize"
              });
            } else if (inlineAttachment.error || !inlineAttachment.content) {
              this.hideInlineImageElement(
                imageElement,
                _t("Could not display image %(attachmentName)s, unsupported attachment type", {
                  attachmentName: inlineAttachment.name
                })
              );
              unsupportedInlineAttachments.push({
                name: inlineAttachment.name,
                reason: inlineAttachment.error ? "fetch_error" : inlineAttachment.format || "unsupported_format"
              });
            } else {
              const mimeType = this.getInlineImageMimeType(inlineAttachment);
              imageElement.setAttribute("src", `data:${mimeType};base64,${inlineAttachment.content}`);
            }
            j++;
          }
        });

        if (unsupportedInlineAttachments.length > 0) {
          console.warn(
            "Some inline Outlook images could not be embedded and were hidden from the logged email.",
            unsupportedInlineAttachments
          );
        }

        if (oversizeAttachments.length > 0) {
          const attachmentNames = oversizeAttachments.map(attachment => `"${attachment.name}"`).join(", ");
          const warningMessage = _t(
            "Warning: Could not fetch the attachments %(attachments)s as their sizes are bigger then the maximum size of %(size)sMB per each attachment.",
            {
              attachments: attachmentNames,
              size: SIZE_THRESHOLD_TOTAL
            }
          );
          doc.body.innerHTML += `<div class="text-danger">${warningMessage}</div>`;
        }

        if (unsupportedAttachments.length > 0) {
          const attachmentNames = unsupportedAttachments.map(attachment => `"${attachment.name}"`).join(", ");
          const warningMessage = _t(
            "Warning: Could not log the attachments %(attachments)s because Outlook returned an unsupported attachment type (for example: cloud/URL attachment).",
            {
              attachments: attachmentNames
            }
          );
          doc.body.innerHTML += `<div class="text-danger">${warningMessage}</div>`;
        }

        requestJson.args[0].body = doc.body.innerHTML;

        const attachmentIds: number[] = [];
        if (attachments.length > 0) {
          const attachmentUploadPromises: Promise<void>[] = [];
          for (let i = 0; i < attachments.length; i++) {
            const uploadPromise = (async () => {
              try {
                const logAttachmentRequest = requestWithAuth(
                  HttpVerb.PATCH,
                  api.baseURL + api.logAttachment,
                  ContentType.Json,
                  {
                    args: [
                      {
                        res_model: this.props.model,
                        res_id: this.props.resId,
                        name: attachments[i].name,
                        mimetype: attachments[i].contentType,
                        // Odoo's `ir.attachment` expects base64 in `datas` (JSON can't carry raw bytes).
                        datas: attachments[i].content
                      }
                    ]
                  }
                );
                const { responseText } = await logAttachmentRequest.promise;
                const parsed = JSON.parse(responseText);
                const hasError = Boolean(parsed["error"]) || parsed.success === false || parsed.success === "False";
                if (hasError) {
                  console.warn("Attachment upload failed, continuing mail log flow.", parsed);
                  return;
                }
                const attachmentId = extractRecordId(responseText);
                if (attachmentId != null) {
                  attachmentIds.push(attachmentId);
                } else {
                  console.warn("Attachment upload succeeded but response contained no id.", parsed);
                }
              } catch (error) {
                console.warn("Attachment upload failed, continuing mail log flow.", error);
              }
            })();
            attachmentUploadPromises.push(uploadPromise);
          }

          await Promise.all(attachmentUploadPromises);
        }

        if (attachmentIds.length > 0) {
          requestJson.args[0].attachment_ids = attachmentIds;
        }

        const sendLogRequest = (payload: any) =>
          requestWithAuth(HttpVerb.PATCH, api.baseURL + api.logSingleMail, ContentType.Json, payload).promise;

        try {
          let logResponse = await sendLogRequest(requestJson);
          let parsed = JSON.parse(logResponse.responseText);
          if (parsed["error"]) {
            // Backwards-compatible retry: old backends may reject attachment_ids.
            const hasAttachmentIds = Array.isArray(requestJson.args[0].attachment_ids);
            const errorText = typeof parsed["error"] === "string" ? parsed["error"] : JSON.stringify(parsed["error"]);
            const looksLikeAttachmentIdsError = hasAttachmentIds && errorText.toLowerCase().includes("attachment_ids");
            if (looksLikeAttachmentIdsError) {
              const fallbackPayload = { ...requestJson, args: [{ ...requestJson.args[0] }] };
              delete fallbackPayload.args[0].attachment_ids;
              logResponse = await sendLogRequest(fallbackPayload);
              parsed = JSON.parse(logResponse.responseText);
            }
          }
          if (parsed["error"]) {
            throw new Error("Could not create mail message");
          }
          this.setState({ logged: 2 });
        } catch (error) {
          this.context.showHttpErrorMessage(error);
          this.setState({ logged: 0 });
        }
      } catch (error) {
        console.error("Unexpected failure while preparing email log payload.", error);
        this.context.showHttpErrorMessage(error);
        this.setState({ logged: 0 });
      }
    });
  };

  private commentRequest = async (event): Promise<any> => {
    event.stopPropagation();

    this.setState({ logged: 0, createCallback: event, isCallOutOpen: true });
  };

  updateLoggedState = status => {
    this.setState({ logged: status });
    this.setState({ isCallOutOpen: false });

    // reset the state after 3 seconds
    setTimeout(() => {
      this.setState({ logged: 0 });
    }, 3000);
  };

  render() {
    let logContainer = null;
    const isCompose = this.context.isComposeMode;
    const isQueued = isCompose && this.isTargetQueued();
    
    switch (this.state.logged) {
      case 0:
        logContainer = (
          <div className="log-container">
            <TooltipHost content="Write a comment">
              <div className="odoo-secondary-button" onClick={this.commentRequest} style={{ width: "auto" }}>
                <FontAwesomeIcon icon={faComment} className={this.state.isCallOutOpen ? "comment-callout" : ""} />
              </div>
            </TooltipHost>
            {isCompose ? (
              this.context.enableLogOnSend ? (
                <TooltipHost content={isQueued ? "Will be logged on send (click to remove)" : "Queue for logging on send"}>
                  <div
                    className={`odoo-secondary-button${isQueued ? " queued" : ""}`}
                    onClick={this.toggleComposeLogTarget}
                    style={{ width: "auto", color: isQueued ? "green" : undefined }}
                  >
                    <FontAwesomeIcon icon={isQueued ? faCheck : faClock} />
                  </div>
                </TooltipHost>
              ) : null
            ) : (
              <TooltipHost content={this.props.tooltipMailContent}>
                <div className="odoo-secondary-button" onClick={this.logRequest} style={{ width: "auto" }}>
                  <FontAwesomeIcon icon={faEnvelope} />
                </div>
              </TooltipHost>
            )}
          </div>
        );
        break;
      case 1:
        logContainer = (
          <div className="log-container">
            <div>
              <Spinner theme={OdooTheme} size={SpinnerSize.medium} />
            </div>
          </div>
        );
        break;
      case 2:
        logContainer = (
          <div className="log-container">
            <TooltipHost content="Write a comment">
              <div className="odoo-secondary-button" onClick={this.commentRequest} style={{ width: "auto" }}>
                <FontAwesomeIcon icon={faComment} />
              </div>
            </TooltipHost>
            <div className="logged-text">
              <FontAwesomeIcon icon={faCheck} color={"green"} />
            </div>
          </div>
        );
        break;
      case 3:
        logContainer = (
          <div className="log-container">
            <div className="logged-text">
              <FontAwesomeIcon icon={faCheck} color={"green"} />
            </div>
            <TooltipHost content={this.props.tooltipMailContent}>
              <div className="odoo-secondary-button" onClick={this.logRequest} style={{ width: "auto" }}>
                <FontAwesomeIcon icon={faEnvelope} />
              </div>
            </TooltipHost>
          </div>
        );
        break;
    }

    return (
      <>
        {logContainer}

        {this.state.isCallOutOpen && (
          <Callout
            directionalHint={DirectionalHint.bottomRightEdge}
            directionalHintFixed={true}
            preventDismissOnScroll={true}
            onDismiss={() => this.setState({ isCallOutOpen: false })}
            setInitialFocus={true}
            doNotLayer={true}
            gapSpace={0}
            className="comment-container"
            role="alertdialog"
            target=".comment-callout"
          >
            <LogCommentInput
              partner={this.props.partner}
              model={this.props.model}
              resId={this.props.resId}
              updateLoggedState={this.updateLoggedState}
            />
          </Callout>
        )}
      </>
    );
  }
}

Logger.contextType = AppContext;

export default Logger;
